/**
 * App
 * contain code that should be seen on all pages. (e.g. navigation bar)
 * This component is the skeleton around the actual pages, and should only
 *
 */

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppWorkspace, AppWrapper } from "./styles";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "common/toast.css";
import {
  isLoggedIn,
  getLoggedInUserLevel,
  getDefaultPathForLevel,
  isStaffUser,
  hasPermission,
  refreshUser,
} from "utils/auth";
import { PERMISSIONS as P } from "common/permissions";
import GlobalStyle from "global-styles";
import { connect } from "react-redux";
import { compose } from "redux";
import injectReducer from "utils/injectReducer";
import injectSaga from "utils/injectSaga";
import { triggerLogout } from "./actions";
import {
  selectLogoutData,
  selectLoginData,
  selectVerifyData,
} from "./selectors";
import { createStructuredSelector } from "reselect";
import saga from "./sagas";
import reducer from "./reducer";
import Login from "screens/Auth/Login/Loadable";
import MyNavbar from "components/MyNavbar";
import Consumer from "screens/Consumer/Loadable";
import Order from "screens/Order/Loadable";
import Queue from "screens/Queue/Loadable";
import QueueAdd from "screens/QueueAdd/Loadable";
import Product from "screens/Product/Loadable";
import Print from "screens/Print/Loadable";
import Home from "screens/Home/Loadable";

import Dashboard from "screens/Dashboard/Loadable";
import User from "screens/Dashboard/User/Loadable";
import Statics from "screens/Dashboard/Statics/Loadable";
import Settings from "screens/Dashboard/Settings/Loadable";
import "../utils/i18n"; // YOUR_OWN_TRACKING_ID
import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import QuickOrder from "screens/QuickOrder/Loadable";
import OrderChecker from "screens/OrderChecker/Loadable";
import BakeBoard from "screens/BakeBoard/Loadable";
import Deliveries from "screens/Deliveries/Loadable";
import DeliverOrder from "screens/DeliverOrder/Loadable";
import CashDrawer from "screens/CashDrawer/Loadable";
import PayDisplay from "screens/PayDisplay/Loadable";
import MyAccount from "screens/MyAccount/Loadable";
import Users from "screens/Users/Loadable";
import Launch from "screens/Launch/Loadable";
import Shop from "screens/Shop/Loadable";
import BuyCake from "screens/BuyCake/Loadable";
import StoreHeader from "components/StoreHeader";
import BulkBanner from "components/BulkBanner";
import Cart from "screens/Cart/Loadable";
import OnlineOrders from "screens/OnlineOrders/Loadable";
import { applyAccountLanguage } from "utils/accountLanguage";
import { trackPage } from "utils/tracking";

// A page for logged-in users; with `perms`, only for those with one of them
// (others go to their own start page). The server checks again on every API.
function PrivateRoute({ children, perms }) {
  if (!isLoggedIn()) return <Navigate to="/login" replace />;
  if (perms && !hasPermission(...perms)) {
    return <Navigate to={getDefaultPathForLevel()} replace />;
  }
  return children;
}

// Google Analytics 4 (G-YHS4S4SKR5) through Firebase; shop events are in
// utils/tracking.js. Started once, not on every render.
const firebaseConfig = {
  apiKey: "AIzaSyCOWXy7dblq8hz-6qXRMZiMFX7Eva47XmU",
  authDomain: "bakery-670d5.firebaseapp.com",
  projectId: "bakery-670d5",
  storageBucket: "bakery-670d5.appspot.com",
  messagingSenderId: "711498623478",
  appId: "1:711498623478:web:892bf2d65d5488a47affd7",
  measurementId: "G-YHS4S4SKR5",
};
getAnalytics(initializeApp(firebaseConfig));

function AppRoot(props) {
  const location = useLocation();
  useEffect(() => {
    trackPage();
  }, [location.pathname]);
  // Role and permissions come fresh from the server on every page load.
  const [, setUserVersion] = useState(0);
  useEffect(() => {
    refreshUser().then((user) => {
      applyAccountLanguage(user);
      setUserVersion((v) => v + 1);
    });
  }, []);
  const currentPathName = !["/", "/home"].includes(location.pathname);

  // Customers get the shop's header on the pages they buy and track from.
  const storeHeader =
    ["/buy-cake", "/cart"].includes(location.pathname) ||
    (["/orders", "/my"].includes(location.pathname) &&
      isLoggedIn() &&
      !isStaffUser());

  return (
    <AppWrapper>
      <Helmet
        titleTemplate="%s - The Oven Delights"
        defaultTitle="The Oven Delights"
      >
        <meta
          name="description"
          content="Best Bakery in Jamshedpur for baking christmas cakes, Order Online !"
        />
      </Helmet>
      {/* {!isLoggedIn() && <Redirect to="/" />} */}

      {storeHeader && <StoreHeader onLogout={props.triggerLogout} />}
      {(isLoggedIn() || location.pathname === "/shop") &&
        !storeHeader &&
        currentPathName && (
          <MyNavbar
            currentPathName={currentPathName}
            onLogout={props.triggerLogout}
            isLoggedIn={isLoggedIn()}
            userLevel={getLoggedInUserLevel()}
          />
        )}

      <AppWorkspace>
        <Routes>
          {/* Public routes */}
          {/* landing page: full screen, no navbar */}
          <Route path="/" element={<Launch />} />
          {/* its old address */}
          <Route path="/launch" element={<Navigate to="/" replace />} />
          <Route element={<Home />}>
            <Route path="/home" element={null} />
            {/* The home page opens the story viewer for this address */}
            <Route path="/stories/:storyId" element={null} />
          </Route>
          <Route path="/about" element={<Home />} />
          {/* the shop and cart work without logging in; ordering needs it */}
          <Route path="/shop" element={<Shop />} />
          <Route path="/buy-cake" element={<BuyCake />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/login" element={<Login />} />
          {/* the old OTP step */}
          <Route path="/verify" element={<Navigate to="/login" replace />} />

          {/* Logged-in pages (with the permission each needs) */}
          <Route
            path="/user"
            element={
              <PrivateRoute perms={[P.USERS]}>
                <User />
              </PrivateRoute>
            }
          />
          <Route
            path="/users"
            element={
              <PrivateRoute perms={[P.USERS]}>
                <Users />
              </PrivateRoute>
            }
          />
          <Route
            path="/statics"
            element={
              <PrivateRoute perms={[P.DASHBOARD]}>
                <Statics />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute perms={[P.DASHBOARD]}>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PrivateRoute perms={[P.DASHBOARD]}>
                <Dashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/consumer"
            element={
              <PrivateRoute perms={[P.QUICK_ORDER]}>
                <Consumer />
              </PrivateRoute>
            }
          />
          <Route
            path="/order"
            element={
              <PrivateRoute perms={[P.QUICK_ORDER]}>
                <Order />
              </PrivateRoute>
            }
          />
          <Route
            path="/queue"
            element={
              <PrivateRoute perms={[P.QUEUE_ADD, P.QUEUE_STATUS]}>
                <Queue />
              </PrivateRoute>
            }
          />
          {/* the token page's old address */}
          <Route path="/queue/add" element={<Navigate to="/token" replace />} />
          <Route
            path="/token"
            element={
              <PrivateRoute perms={[P.QUEUE_ADD]}>
                <QueueAdd />
              </PrivateRoute>
            }
          />
          <Route
            path="/product"
            element={
              <PrivateRoute perms={[P.PRODUCTS]}>
                <Product />
              </PrivateRoute>
            }
          />
          <Route
            path="/print"
            element={
              <PrivateRoute perms={[P.QUICK_ORDER]}>
                <Print />
              </PrivateRoute>
            }
          />
          {/* Old shop pages, replaced by /buy-cake */}
          <Route path="/buy/*" element={<Navigate to="/buy-cake" replace />} />
          <Route
            path="/quick-order"
            element={
              <PrivateRoute perms={[P.QUICK_ORDER]}>
                <QuickOrder />
              </PrivateRoute>
            }
          />
          <Route
            path="/order-checker"
            element={
              <PrivateRoute perms={[P.ORDER_CHECK]}>
                <OrderChecker />
              </PrivateRoute>
            }
          />
          <Route
            path="/bake"
            element={
              <PrivateRoute perms={[P.BAKE_BOARD]}>
                <BakeBoard />
              </PrivateRoute>
            }
          />
          <Route
            path="/deliveries"
            element={
              <PrivateRoute perms={[P.DELIVERY, P.BAKE_BOARD]}>
                <Deliveries />
              </PrivateRoute>
            }
          />
          <Route
            path="/deliver/:orderId/:step?"
            element={
              <PrivateRoute perms={[P.DELIVERY]}>
                <DeliverOrder />
              </PrivateRoute>
            }
          />
          <Route
            path="/pay-display"
            element={
              <PrivateRoute perms={[P.QUICK_ORDER, P.DELIVERY]}>
                <PayDisplay />
              </PrivateRoute>
            }
          />
          <Route
            path="/cash-drawer"
            element={
              <PrivateRoute perms={[P.DELIVERY]}>
                <CashDrawer />
              </PrivateRoute>
            }
          />
          <Route
            path="/online-orders"
            element={
              <PrivateRoute perms={[P.QUICK_ORDER, P.DELIVERY]}>
                <OnlineOrders />
              </PrivateRoute>
            }
          />
          <Route
            path="/orders"
            element={
              <PrivateRoute>
                <MyAccount />
              </PrivateRoute>
            }
          />
          <Route
            path="/my"
            element={
              <PrivateRoute>
                <MyAccount />
              </PrivateRoute>
            }
          />

          <Route path="*" element={<div>Not Found</div>} />
        </Routes>
        <BulkBanner />
        <ToastContainer />
      </AppWorkspace>

      <GlobalStyle />
    </AppWrapper>
  );
}

const mapStateToProps = createStructuredSelector({
  logoutData: selectLogoutData(),
  loginData: selectLoginData(),
  verifyData: selectVerifyData(),
});

function mapDispatchToProps(dispatch) {
  return {
    triggerLogout: (data) => {
      dispatch(triggerLogout(data));
    },
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

const withReducer = injectReducer({ key: "logout", reducer });
const withSaga = injectSaga({ key: "logout", saga });

export default compose(withReducer, withSaga, withConnect)(AppRoot);
